import {
	DATABASE_VIEW,
	VIEW_PAGE,
	WHITEBOARD_BOARD,
	SLIDES_VIEW,
	EMBED_VIEW,
	EDIT_PAGE,
	EDIT_PAGE_V2,
} from '@confluence/named-routes/entry-points/routes.ts';
import { FeedbackSource } from '@confluence/global-feedback-collector/entry-points/feedbackSourceType.ts';

export enum ContentType {
	WHITEBOARD = 'whiteboard',
	PAGE = 'page',
	BLOGPOST = 'blogpost',
	DATABASE = 'database',
	SLIDE = 'slide',
	EMBED = 'embed',
	FOLDER = 'folder',
}

/**
 * Routes that we might redirect from after successfully archiving a piece of content in the tree
 * Full logic for determining this behavior lives in `@confluence/archive-pages`
 */
export const ARCHIVE_SUCCESS_REDIRECT_ROUTES: string[] = [
	DATABASE_VIEW.name,
	VIEW_PAGE.name,
	WHITEBOARD_BOARD.name,
	SLIDES_VIEW.name,
	EMBED_VIEW.name,
];

/**
 * Routes that we might redirect from after successfully deleting a parent in the tree
 * while viewing the child content
 * Full logic for determining this behavior lives in `@confluence/bulk-page-actions`
 */
export const DELETE_SUCCESS_REDIRECT_ROUTES: string[] = [
	DATABASE_VIEW.name,
	VIEW_PAGE.name,
	WHITEBOARD_BOARD.name,
	SLIDES_VIEW.name,
	EMBED_VIEW.name,
	EDIT_PAGE.name,
	EDIT_PAGE_V2.name,
];

export const VALID_OPERATION_TARGETS: ContentType[] = [
	ContentType.WHITEBOARD,
	ContentType.DATABASE,
	ContentType.SLIDE,
	ContentType.EMBED,
	ContentType.PAGE,
	ContentType.FOLDER,
	ContentType.BLOGPOST,
];

/**
 * Feedback sources for the Global Feedback Collector for each content type
 */
export const CONTENT_TYPE_FEEDBACK_SOURCE: {
	whiteboard: FeedbackSource;
	embed: FeedbackSource;
	database: FeedbackSource;
	slide: FeedbackSource;
} = {
	[ContentType.WHITEBOARD]: FeedbackSource.WHITEBOARD,
	[ContentType.EMBED]: FeedbackSource.EMBED,
	[ContentType.DATABASE]: FeedbackSource.DATABASE,
	[ContentType.SLIDE]: FeedbackSource.SLIDE,
};

/**
 * Canvas-rendered content types — content rendered on the WebGL canvas surface (whiteboard/slides).
 * Use this for checks that apply equally to whiteboards and slides but NOT databases.
 */
export const CANVAS_CONTENT_TYPES: string[] = [ContentType.WHITEBOARD, ContentType.SLIDE];

export const isCanvasContentType = (
	contentType: string | null | undefined,
): contentType is 'whiteboard' | 'slide' =>
	!!contentType && CANVAS_CONTENT_TYPES.includes(contentType);

/**
 * Resource types where the ARIs include activationIds. This is a pattern
 * that started with Confluence Databases.
 */
export const TYPES_WITH_ACTIVATIONID: string[] = ['database', 'slide', 'embed', 'folder'];

export const onChangeTitleDebounceTime = 400;
